import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

export default function Kontakt() {
  useSEO(
    'Kontakt os | Chokolenten ApS',
    'Kontakt Chokolenten ApS i Hanstholm. Ring, send en e-mail eller udfyld vores kontaktformular for bestillinger af chokolade, Thy Kukies og kager.'
  );

  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });



  // Præ-udfyld beskrivelse hvis vi kommer fra et produktkort
  useEffect(() => {
    if (location.state && location.state.productTitle) {
      setFormData(prev => ({
        ...prev,
        description: `Hej Chokolenten,\n\nJeg vil meget gerne bestille jeres lækre "${location.state.productTitle}".\n\nKontakt mig venligst angående afhentning og betaling.\n\nMvh.`
      }));
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Enkel validering
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst alle påkrævede felter.'
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({
          submitting: false,
          success: true,
          error: null
        });
        // Nulstil form (behold dog honeypot tom)
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        throw new Error(result.message || 'Der skete en fejl under afsendelsen. Prøv igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der kunne ikke oprettes forbindelse til serveren. Prøv igen senere.'
      });
    }
  };

  return (
    <div className="pb-20">
      
      {/* Hero Section */}
      <section className="relative py-24 bg-choc-dark text-cream-light overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/dulche-salt.jpg"
            alt="Kontakt os baggrund"
            className="w-full h-full object-cover opacity-50 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-choc-dark/60 via-choc-dark/30 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-4">
          <span className="text-xs font-sans uppercase tracking-[0.25em] text-berry-light font-bold">
            Skriv eller ring
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-cream-light tracking-wide">
            Kontakt os
          </h1>
          <p className="max-w-2xl text-sm sm:text-base text-cream-light/80 leading-relaxed font-sans">
            Har du en forespørgsel på en specialbestilling, ris eller ros, eller vil du bare høre mere om vores udvalg? Vi glæder os til at høre fra dig!
          </p>
        </div>
      </section>

      {/* Kontakt Indhold */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          
          {/* Venstre: Info & Detaljer */}
          <div className="lg:col-span-5 flex flex-col gap-10">
            <div className="flex flex-col gap-4">
              <span className="text-xs font-sans uppercase tracking-[0.2em] text-berry-accent font-bold">
                Find os her
              </span>
              <h2 className="text-3xl font-serif font-bold text-choc-dark">
                Kig forbi butikken
              </h2>
              <p className="text-base text-choc-dark/70 font-sans leading-relaxed">
                Vores hyggelige butik og chokoladeri ligger centralt på Bytorvet i Hanstholm. Du er altid velkommen til at kigge forbi til en snak og en smagsprøve!
              </p>
            </div>

            {/* Kontaktoplysninger */}
            <div className="flex flex-col gap-6 bg-cream-light p-8 rounded-2xl border border-choc-dark/10 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent flex-shrink-0">
                  <i className="fa-solid fa-location-dot text-lg" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-lg text-choc-dark mb-1">Adresse</h4>
                  <p className="text-sm text-choc-dark/80 font-sans">Bytorvet 30, 7730 Hanstholm</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent flex-shrink-0">
                  <i className="fa-solid fa-id-card text-lg" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-lg text-choc-dark mb-1">CVR-nummer</h4>
                  <p className="text-sm text-choc-dark/80 font-sans font-medium">44157438</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent flex-shrink-0">
                  <i className="fa-solid fa-phone text-lg" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-lg text-choc-dark mb-1">Telefon</h4>
                  <a
                    href="tel:+4597960600"
                    className="text-sm text-choc-dark/80 font-sans hover:text-berry-accent transition-colors whitespace-nowrap font-medium"
                  >
                    97 96 06 00
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent flex-shrink-0">
                  <i className="fa-solid fa-envelope text-lg" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-lg text-choc-dark mb-1">E-mail</h4>
                  <a
                    href="mailto:info@chokolenten.dk"
                    className="text-sm text-choc-dark/80 font-sans hover:text-berry-accent transition-colors whitespace-nowrap font-medium"
                  >
                    info@chokolenten.dk
                  </a>
                </div>
              </div>

              {/* Fødevarestyrelsens Smiley Rapport */}
              <div className="pt-4 border-t border-choc-dark/10 flex flex-col gap-2">
                <a
                  href="https://www.findsmiley.dk/1391306"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block group"
                  title="Se Fødevarestyrelsens kontrolrapport for Chokolenten"
                >
                  <img
                    src="https://assets.auxodev.dk/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/banner-groen-847x274.jpg"
                    alt="Fødevarestyrelsens Smiley Rapport"
                    className="h-14 w-auto object-contain rounded border border-choc-dark/10 group-hover:shadow-md transition-all duration-200"
                  />
                </a>
              </div>
            </div>

            {/* Sociale Medier */}
            <div className="flex flex-col gap-4">
              <h4 className="font-serif font-bold text-xl text-choc-dark">Følg os på sociale medier</h4>
              <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                Vi lægger løbende billeder op af vores nyeste kreationer og fortæller om hverdagen i butikken. Følg os på <strong className="text-berry-accent">@Chokolenten</strong>.
              </p>
              <div className="flex items-center gap-4 mt-1">
                <a
                  href="https://www.facebook.com/chokolenten"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-cream-light border border-choc-dark/10 flex items-center justify-center text-choc-dark hover:bg-berry-accent hover:text-white hover:border-berry-accent transition-all duration-300 shadow-sm"
                  aria-label="Følg os på Facebook"
                >
                  <i className="fa-brands fa-facebook-f text-lg" />
                </a>
                <a
                  href="https://www.instagram.com/chokolenten/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-cream-light border border-choc-dark/10 flex items-center justify-center text-choc-dark hover:bg-berry-accent hover:text-white hover:border-berry-accent transition-all duration-300 shadow-sm"
                  aria-label="Følg os på Instagram"
                >
                  <i className="fa-brands fa-instagram text-lg" />
                </a>
                <a
                  href="https://www.tiktok.com/@chokolenten"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-cream-light border border-choc-dark/10 flex items-center justify-center text-choc-dark hover:bg-berry-accent hover:text-white hover:border-berry-accent transition-all duration-300 shadow-sm"
                  aria-label="Følg os på TikTok"
                >
                  <i className="fa-brands fa-tiktok text-lg" />
                </a>
              </div>
            </div>
          </div>

          {/* Højre: Formular */}
          <div className="lg:col-span-7 bg-cream-light p-8 sm:p-10 rounded-2xl border border-choc-dark/10 shadow-sm">
            <h3 className="font-serif font-bold text-2xl text-choc-dark mb-2">
              Send os en besked
            </h3>
            <p className="text-sm text-choc-dark/70 font-sans mb-8">
              Udfyld formularen nedenfor, så vender vi tilbage til dig så hurtigt som muligt. Felter markeret med <span className="text-berry-accent">*</span> skal udfyldes.
            </p>

            {status.success ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-xl flex flex-col gap-3 font-sans animate-in fade-in duration-200">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-xl text-emerald-600" />
                  <h4 className="font-bold text-lg">Tak for din besked!</h4>
                </div>
                <p className="text-sm leading-relaxed">
                  Vi har modtaget din henvendelse og vil læse den igennem hurtigst muligt. Vi vender tilbage til dig inden for kort tid.
                </p>
                <button
                  onClick={() => setStatus({ submitting: false, success: false, error: null })}
                  className="mt-2 text-xs font-bold text-emerald-700 hover:underline text-left cursor-pointer"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                
                {/* ANTISPAM HONEYPOT - ABSOLUT SKJULT */}
                <div className="sr-only absolute" style={{ width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                  <label htmlFor="website_url">Efterlad dette felt tomt</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Navn */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-xs font-sans font-bold uppercase tracking-wider text-choc-dark/70">
                    Navn <span className="text-berry-accent">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Dit fulde navn"
                    className="w-full bg-cream-bg border border-choc-dark/15 rounded-xl px-4 py-3.5 text-sm font-sans text-choc-dark focus:outline-none focus:border-berry-accent focus:ring-1 focus:ring-berry-accent transition-all"
                  />
                </div>

                {/* Telefon & Email i række på desktop */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Telefonnummer */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="phone" className="text-xs font-sans font-bold uppercase tracking-wider text-choc-dark/70">
                      Telefonnummer <span className="text-berry-accent">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Dit telefonnummer"
                      className="w-full bg-cream-bg border border-choc-dark/15 rounded-xl px-4 py-3.5 text-sm font-sans text-choc-dark focus:outline-none focus:border-berry-accent focus:ring-1 focus:ring-berry-accent transition-all"
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-xs font-sans font-bold uppercase tracking-wider text-choc-dark/70">
                      E-mailadresse <span className="text-berry-accent">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="din@email.dk"
                      className="w-full bg-cream-bg border border-choc-dark/15 rounded-xl px-4 py-3.5 text-sm font-sans text-choc-dark focus:outline-none focus:border-berry-accent focus:ring-1 focus:ring-berry-accent transition-all"
                    />
                  </div>
                </div>

                {/* Beskrivelse */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="description" className="text-xs font-sans font-bold uppercase tracking-wider text-choc-dark/70">
                    Beskrivelse / Bestilling <span className="text-berry-accent">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Skriv din besked eller bestilling her..."
                    className="w-full bg-cream-bg border border-choc-dark/15 rounded-xl px-4 py-3.5 text-sm font-sans text-choc-dark focus:outline-none focus:border-berry-accent focus:ring-1 focus:ring-berry-accent transition-all resize-y"
                  />
                </div>

                {/* Fejlmeddelelse */}
                {status.error && (
                  <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl flex items-center gap-2 text-sm font-sans animate-in fade-in duration-200">
                    <i className="fa-solid fa-circle-exclamation text-rose-600 flex-shrink-0" />
                    <span>{status.error}</span>
                  </div>
                )}

                {/* Submit Knap */}
                <button
                  type="submit"
                  disabled={status.submitting}
                  className="w-full bg-berry-accent hover:bg-berry-accent/90 disabled:bg-berry-accent/50 text-white font-sans font-semibold py-4 px-6 rounded-xl transition-colors shadow-md hover:shadow-lg cursor-pointer flex items-center justify-center gap-2"
                >
                  {status.submitting ? (
                    <>
                      <div className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                      <span>Sender besked...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane text-sm" />
                      <span>Send besked / bestilling</span>
                    </>
                  )}
                </button>

              </form>
            )}
          </div>

        </div>
      </section>



      {/* Bestillings- og leveringsinfo sektion */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="bg-cream-light rounded-2xl border border-choc-dark/10 shadow-sm p-8 sm:p-12">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <div className="lg:w-1/3 flex flex-col gap-4">
              <span className="text-xs font-sans uppercase tracking-[0.2em] text-berry-accent font-bold">
                Erhverv & Bestilling
              </span>
              <h3 className="text-3xl font-serif font-bold text-choc-dark">
                Værd at vide om bestilling & levering
              </h3>
              <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                Vi leverer friskbagte Kukies og håndlavede chokolader til butikker, caféer og virksomheder. Her er de vigtigste betingelser for vores leverancer.
              </p>
              <div className="mt-4">
                <Link
                  to="/handelsbetingelser"
                  className="text-sm font-bold text-berry-accent hover:underline flex items-center gap-2"
                >
                  <span>Læs de fulde handelsbetingelser</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

            <div className="lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-8 w-full">
              {/* Bestilling & Levering */}
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent flex-shrink-0">
                  <i className="fa-solid fa-calendar-days text-lg" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <h4 className="font-serif font-bold text-lg text-choc-dark">Bestilling & Levering</h4>
                  <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                    Bestilles senest <strong className="text-choc-dark">mandag kl. 10:00</strong>. Alle produkter produceres efter bestilling. Levering sker typisk <strong className="text-choc-dark">torsdag</strong> med Danske Fragtmænd.
                  </p>
                </div>
              </div>

              {/* Betaling */}
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent flex-shrink-0">
                  <i className="fa-solid fa-file-invoice-dollar text-lg" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <h4 className="font-serif font-bold text-lg text-choc-dark">Betalingsbetingelser</h4>
                  <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                    Betaling er <strong className="text-choc-dark">Netto 8 dage</strong>. Nye leverancer sendes først, når tidligere udestående fakturaer er betalt.
                  </p>
                </div>
              </div>

              {/* Håndlavet kvalitet */}
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent flex-shrink-0">
                  <i className="fa-solid fa-sparkles text-lg" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <h4 className="font-serif font-bold text-lg text-choc-dark">Håndlavet kvalitet</h4>
                  <p className="text-sm text-choc-dark/70 font-sans leading-relaxed">
                    Håndlavede produkter varierer naturligt i størrelse og udseende. Vores Kukies kan være både sprøde og bløde, og leveres i æsker med 10 stk.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
